import { Link, Text } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import type { Brand } from '@/domainLogic/index.ts';
import customCss from '@/app/components/hotel-detail/css/customCss';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import BasicModalHeader from '@/app/components/search/components/Modal/LayoutElements/BasicModalHeader';
import { useTranslation } from '@/app/components/translations/Translation';
import { useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { Icon } from '@/app/common/icons/Icon';
import { FLASH_SALES_ICON, flashSalesIconSize } from '@/app/common/icons/promotionIcons';
import LastMinuteDealsBadge from '@/app/common/components/Promotions/LastMinuteDealsBadge';
import type { BrandedFare } from '@/app/api/hotel-detail/branded-fares/route';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import { LmnDealMessage } from '@/app/components/hotel-detail/LmnDealMessage';
import { PeriodicPromotion } from '@/app/common/components/Promotions/LastMinuteDealsBadge/PeriodicPromotion';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { BackToTravelMessage } from '../BackToTravel';
import useUserConfig from '../hooks/useUserConfig';
import { PriceBreakdownContent } from './LocalTaxes';
import { TaxesAllIncludedBreakdown } from './TaxesAllIncludedBreakdown';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useFetchHotelPromotions, useHotelPromotion } from '../hooks/useFetchHotelPromotions';
import { useOffersContext } from '../OffersProvider';

export function PackageDetailsModal(props: {
  cheapestMealPlan: MealPlanType;
  roomsIncludedInPrice: number;
  nights: number;
  passengers: number;
  showSmallAmountDeposit: boolean;
  isOpen: boolean;
  onClose: () => void;
  onOpenByBrowserHistory?: () => void;
  selectedBrandedFare?: BrandedFare; // ATTENTION: here we are using BrandedFares but from
  // package review step (PackageReviewExtras), never from room details (PackageDetails)
}) {
  const {
    cheapestMealPlan,
    roomsIncludedInPrice,
    nights,
    passengers,
    showSmallAmountDeposit,
    isOpen,
    onClose,
    onOpenByBrowserHistory,
    selectedBrandedFare,
  } = props;

  const urlSearchParams = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const { commonData } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { t } = useTranslation();

  return (
    <OpenXModal
      isOpen={isOpen ?? false}
      isMobile={isMobile ?? true}
      brand={localization.brand as Brand}
      onClose={onClose}
      onCloseByBrowserHistory={onClose}
      onOpenByBrowserHistory={onOpenByBrowserHistory}
      fullScreen={false}
      className="package-details-modal"
      id="package-details-modal"
      aria-label={t('vacation.select.summary.modal.title')}
      bodyBackgroundColor="backgroundPage"
      header={{
        sticky: true,
        element: (
          <BasicModalHeader removeShadow={true} handleClose={onClose}>
            {t('vacation.select.summary.modal.title')}
          </BasicModalHeader>
        ),
      }}
    >
      <PackageDetailsModalContainer
        cheapestMealPlan={cheapestMealPlan}
        roomsIncludedInPrice={roomsIncludedInPrice}
        nights={nights}
        passengers={passengers}
        showSmallAmountDeposit={showSmallAmountDeposit}
        hotelOnly={urlSearchParams.accommodationOnly}
        selectedBrandedFare={selectedBrandedFare}
      />
    </OpenXModal>
  );
}

function PackageDetailsModalContainer(props: {
  cheapestMealPlan: MealPlanType;
  roomsIncludedInPrice: number;
  nights: number;
  passengers: number;
  showSmallAmountDeposit: boolean;
  hotelOnly: boolean;
  selectedBrandedFare?: BrandedFare;
}) {
  const {
    cheapestMealPlan,
    roomsIncludedInPrice,
    nights,
    showSmallAmountDeposit,
    passengers,
    hotelOnly,
    selectedBrandedFare,
  } = props;
  // const [selectedTab, setSelectedTab] = useState<'TRAVEL_EXTRA_PACK' | 'PRICE_BREAKDOWN' | 'DEPOSIT'>(
  //   'TRAVEL_EXTRA_PACK'
  // );
  const { t } = useTranslation();
  const { showBackToTravel, isDoublePriceDP } = useUserConfig();
  const { showLmnDealsPromotion, showFlashSalesPromotion } = useHotelPromotion();
  const totalPrice = isDoublePriceDP ? cheapestMealPlan.priceWithoutTaxes : cheapestMealPlan.finalTotalPrice;
  const taxesTotal = cheapestMealPlan.excludedCharges?.amount;
  const { offersData } = useOffersContext();
  const { localization } = usePageWrapperData();
  const taxesIncluded =
    localization?.configValuesApp?.taxesIncludedPopup || localization?.configValuesApp?.taxesIncludedDoublePricing;

  const showPromotionLayout = showLmnDealsPromotion ? offersData?.hasFlashSaleOffers : showFlashSalesPromotion;
  const showDeferredPayments = !!cheapestMealPlan?.deferredPaymentDeposit;

  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
      `}
    >
      <PackageDetailsContent>
        {showPromotionLayout && (
          <SectionContent>
            {showLmnDealsPromotion ? (
              <LastMinuteDealsBadge
                fontPreset={css`
                  font-size: 16px;
                  line-height: 24px;
                `}
                iconSize={20}
              />
            ) : (
              <>
                <div
                  css={`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <Icon {...FLASH_SALES_ICON} size={flashSalesIconSize(20)} footprint={20} alt="lastminute deal" />

                  <Text
                    variant="Title4"
                    css={`
                      flex-grow: 1;
                      grid-area: 1/2/1/3;
                    `}
                  >
                    {t('vacation.select.flashsales.banner.title')}
                  </Text>
                </div>
                <PeriodicPromotion />
              </>
            )}
            <PromotionDealContent />
          </SectionContent>
        )}
        {showBackToTravel && cheapestMealPlan.backToTravel && (
          <SectionContent>
            <Text variant="Title4">{t('vacation.select.package_savings.travel_extra_pack.modal.title')}</Text>
            <BackToTravelMessage
              {...cheapestMealPlan.backToTravel}
              {...(selectedBrandedFare && {
                finalPrice: cheapestMealPlan.backToTravel.finalPrice + selectedBrandedFare.priceDifference,
                strikeThroughPrice:
                  cheapestMealPlan.backToTravel.strikeThroughPrice + selectedBrandedFare.priceDifference,
              })}
              css={`
                background-color: white;
                border-radius: 8px;
              `}
            />
          </SectionContent>
        )}
        {cheapestMealPlan.excludedCharges &&
          cheapestMealPlan.excludedCharges.amount > 0 &&
          cheapestMealPlan.priceWithoutTaxes && (
            <SectionContent>
              <Text variant="Title4">{t('vacation.common.citytaxes.package.price.details.title')}</Text>
              {taxesIncluded ? (
                <div
                  css={`
                    background-color: white;
                    border-radius: 8px;
                    padding: 16px;
                  `}
                >
                  <TaxesAllIncludedBreakdown
                    pricePerPerson={cheapestMealPlan.priceWithoutTaxes}
                    taxesPerPerson={taxesTotal ?? 0}
                  />
                </div>
              ) : (
                <PriceBreakdownContent
                  roomsIncludedInPrice={roomsIncludedInPrice}
                  nights={nights}
                  priceWithoutTaxes={cheapestMealPlan.priceWithoutTaxes}
                  totalPrice={totalPrice ?? 0}
                  taxesIncludedInFinalPrice={cheapestMealPlan.excludedChargesIncluded}
                  passengers={passengers}
                  taxesTotal={taxesTotal ?? 0}
                  hotelOnly={hotelOnly}
                  css={`
                    background-color: white;
                    border-radius: 8px;
                    padding: 16px;
                  `}
                />
              )}
            </SectionContent>
          )}
        {showDeferredPayments && (
          <SectionContent>
            <Text variant="Title4">{t('vacation.select.summary.modal.deposit.title')}</Text>
            <DeferredPaymentContent showSmallAmountDeposit={showSmallAmountDeposit} />
          </SectionContent>
        )}
      </PackageDetailsContent>
    </div>
  );
}

function DeferredPaymentContent(props: { showSmallAmountDeposit: boolean }) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { showSmallAmountDeposit } = props;
  return (
    <div
      css={`
        background-color: white;
        border-radius: 8px;
        padding: 16px;
      `}
    >
      <TextBase>
        {showSmallAmountDeposit
          ? t('vacation.select.details.deferredpayments.banner.min_deposit.subtitle', {
              amount: formatPrice({ price: 1, localization }),
            })
          : t('vacation.select.details.deferredpayments.banner.subtitle')}
      </TextBase>
      {!showSmallAmountDeposit && localization?.deferredPaymentLink ? (
        <div
          css={`
            margin-top: 4px;
          `}
        >
          <Link
            href={localization.deferredPaymentLink}
            target="_blank"
            variant="accent"
            rel="noopener noreferrer"
            css={css`
              ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
            `}
          >
            {t('vacation.select.details.deferredpayments.banner.link')}
          </Link>
        </div>
      ) : undefined}
    </div>
  );
}

function PromotionDealContent() {
  const { data: promo } = useFetchHotelPromotions();
  return (
    promo &&
    promo.bookDate && (
      <div
        css={`
          background-color: white;
          border-radius: 8px;
          padding: 16px;
        `}
      >
        <TextBase>
          <LmnDealMessage bookDate={promo.bookDate} checkin={promo.checkin} checkout={promo.checkout} />
        </TextBase>
      </div>
    )
  );
}

const PackageDetailsContent = styled.div`
  /* padding: 64px 16px 24px 16px; */
  padding: 16px 16px 24px 16px;
  background-color: ${(p) => p.theme.commons.colors.gray50};
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${customCss.breakpoints.sm} {
    padding: 16px 16px 24px 16px;
  }
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
